import type { ReactNode } from "react";
import { formatBytes } from "@/lib/utils";

// Title slot written by InlineImageRewriter: ilp-file|contentType|byteSize.
// It is a carrier, never a tooltip.
const FILE_LINK_TITLE = /^ilp-file\|([^|]*)\|(\d*)$/;

const FILE_TYPE_LABELS: Record<string, string> = {
  "application/pdf": "PDF",
  "application/msword": "Word",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "Word",
  "application/vnd.ms-excel": "Excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel",
  "application/vnd.ms-powerpoint": "PowerPoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "PowerPoint",
};

interface StoryboardLinkProps {
  href?: string;
  title?: string;
  children?: ReactNode;
  className?: string;
}

interface FileLinkMetadata {
  contentType: string;
  byteSize: string;
}

const fileLinkMetadata = (
  title: string | undefined
): FileLinkMetadata | null => {
  const match = FILE_LINK_TITLE.exec(title ?? "");
  if (!match) return null;

  return { contentType: match[1], byteSize: match[2] };
};

const isUnresolvedFileToken = (href: string | undefined) =>
  Boolean(href?.startsWith("ilp-file://"));

const typeLabelFor = (contentType: string) =>
  FILE_TYPE_LABELS[contentType] ?? "";

const sizeLabelFor = (byteSize: string) => {
  if (!byteSize) return "";

  const bytes = Number(byteSize);
  if (!Number.isFinite(bytes)) return "";

  return formatBytes(bytes);
};

const metadataLabel = (contentType: string, byteSize: string) => {
  const typeLabel = typeLabelFor(contentType);
  const sizeLabel = sizeLabelFor(byteSize);
  if (typeLabel && sizeLabel) return `${typeLabel}, ${sizeLabel}`;

  return typeLabel || sizeLabel;
};

const renderMetadata = (contentType: string, byteSize: string) => {
  const label = metadataLabel(contentType, byteSize);
  if (!label) return null;

  return <span className="text-muted-foreground"> ({label})</span>;
};

const renderPlainAnchor = (
  href: string | undefined,
  children: ReactNode,
  className?: string
) => (
  // Omit empty class so global `a:not([class])` content-link styles match.
  <a href={href} className={className || undefined}>
    {children}
  </a>
);

const renderResolvedFileLink = (
  href: string,
  contentType: string,
  byteSize: string,
  children: ReactNode,
  className?: string
) => (
  <>
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className || undefined}
    >
      <span aria-hidden="true">↓ </span>
      {children} <span className="sr-only">öffnet in neuem Tab</span>
    </a>
    {renderMetadata(contentType, byteSize)}
  </>
);

const renderUnresolvedFileToken = (children: ReactNode) => (
  <span>{children}</span>
);

/**
 * Lesson anchor. A downloaded file shows a down arrow, the author label, and
 * gray type and size. An ordinary link stays a plain anchor. A file that was
 * never imported stays the author label as text.
 */
const StoryboardLink = ({
  href,
  title,
  children,
  className,
}: StoryboardLinkProps) => {
  if (isUnresolvedFileToken(href)) {
    return renderUnresolvedFileToken(children);
  }

  const metadata = fileLinkMetadata(title);
  if (metadata && href) {
    return renderResolvedFileLink(
      href,
      metadata.contentType,
      metadata.byteSize,
      children,
      className
    );
  }

  return renderPlainAnchor(href, children, className);
};

export default StoryboardLink;
